/**
 * Sign-in for the web surface (build plan decision 5). Inside Teams the tab asks Teams for a token
 * (tab SSO, audience api://<host>/<client id> per the manifest's webApplicationInfo); in a browser MSAL
 * runs the redirect flow for api://<client id>/access_as_user. Either way the API gets a bearer token
 * for the signed-in person and nothing else identifies them.
 */
import { InteractionRequiredAuthError, PublicClientApplication } from "@azure/msal-browser";
import { app as teamsApp, authentication as teamsAuth } from "@microsoft/teams-js";

export interface Config { clientId: string | null; tenantId: string | null; scope: string | null; teamsResource: string | null; signInConfigured: boolean }

export interface Session {
  mode: "teams" | "browser";
  /** A fresh access token for the API. */
  token(): Promise<string>;
  account?: string;
  /** Ends the browser session. Absent inside Teams, where the host owns the sign-in. */
  signOut?: () => Promise<void>;
}

/** Resolves to a session, or to "redirecting" when MSAL is about to leave the page for the sign-in. */
export async function signIn(config: Config): Promise<Session | "redirecting"> {
  if (!config.clientId || !config.tenantId || !config.scope) throw new Error("Sign-in is not configured on this API (TRELLIS_APP_ID, TRELLIS_TENANT_ID).");
  if (await insideTeams()) {
    const token = () => teamsAuth.getAuthToken();
    await token(); // fail early, before the screen renders, so the error is the first thing shown
    teamsApp.notifySuccess();
    return { mode: "teams", token };
  }
  return browserSignIn(config.clientId, config.tenantId, config.scope);
}

/** Teams answers `initialize` only when it is the host; anywhere else the call never resolves, so it is raced. */
async function insideTeams(): Promise<boolean> {
  if (window.self === window.top && !/Teams/i.test(navigator.userAgent)) return false;
  const timeout = new Promise<false>((r) => setTimeout(() => r(false), 2500));
  try { return await Promise.race([teamsApp.initialize().then(() => true), timeout]); } catch { return false; }
}

async function browserSignIn(clientId: string, tenantId: string, scope: string): Promise<Session | "redirecting"> {
  const pca = new PublicClientApplication({
    auth: { clientId, authority: `https://login.microsoftonline.com/${tenantId}`, redirectUri: `${location.origin}/app`, navigateToLoginRequestUrl: true },
    cache: { cacheLocation: "sessionStorage" },
  });
  await pca.initialize();
  const fromRedirect = await pca.handleRedirectPromise();
  const account = fromRedirect?.account ?? pca.getAllAccounts()[0];
  if (!account) { await pca.loginRedirect({ scopes: [scope] }); return "redirecting"; }
  pca.setActiveAccount(account);
  const token = async () => {
    try {
      return (await pca.acquireTokenSilent({ scopes: [scope], account })).accessToken;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) { await pca.acquireTokenRedirect({ scopes: [scope], account }); }
      throw e;
    }
  };
  const signOut = () => pca.logoutRedirect({ account, postLogoutRedirectUri: `${location.origin}/app` });
  return { mode: "browser", token, account: account.username, signOut };
}
